import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Link2, Check } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { BatchResult, localBatch } from "@/components/BatchResult";
import { BatchForm, useBatchLookup } from "@/components/Ledger";
import { PageShell, LegacyShell } from "@/components/PageShell";
import { BatchNotice } from "@/pages/OurStandard";

export default function BatchPage() {
  const { batchNo } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [batch, setBatch] = useState("");
  const [copied, setCopied] = useState(false);
  const { result, error, busy, lookup } = useBatchLookup(t);

  useEffect(() => {
    lookup(batchNo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchNo]);
  useEffect(() => {
    const b = localBatch(result, lang);
    const real = b && !result?.sample;
    document.title = real ? `Batch ${b.batch_no} | Dirty Paws Apothecary` : "Batch lookup | Dirty Paws Apothecary";
    let robots = document.head.querySelector('meta[name="robots"]');
    if (!robots) { robots = document.createElement("meta"); robots.name = "robots"; document.head.appendChild(robots); }
    robots.content = real ? "index, follow" : "noindex, follow";
  }, [result, lang]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard unavailable */ }
  };

  if (!result || result.sample) {
    return (
      <PageShell testid="batch-page" page="standard">
        <div className="wrap" style={{ padding: "56px 40px" }}><BatchNotice /></div>
      </PageShell>
    );
  }

  return (
    <LegacyShell testid="batch-page" noindex>
      <main className="max-w-3xl mx-auto px-5 md:px-10 py-16 md:py-20" data-no-translate>
        <p className="font-display italic text-[#B5A642]">{t("From the Batch Ledger", "Du registre des lots")}</p>
        <p className="mt-2 font-ledger text-[12px] uppercase tracking-[0.25em] text-[#D3CEC4]/60">{t("Batch", "Lot")} {batchNo?.toUpperCase()}</p>
        <div className="mt-8 border border-[#303B2D] rounded-sm bg-[#161918] p-7 md:p-10">
          {busy && <p className="text-[#D3CEC4]/55">{t("Consulting the ledger…", "Consultation du registre…")}</p>}
          {error && <p data-testid="ledger-error" className="font-display italic text-xl text-[#D3CEC4]/65">{error}</p>}
          {result && <BatchResult result={result} t={t} lang={lang} />}
          {result && (
            <button onClick={copy} data-testid="batch-copy-link"
              className="mt-6 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#B5A642] hover:text-[#E8DDC7] transition-colors">
              {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
              {copied ? t("Link copied", "Lien copié") : t("Copy this batch's link", "Copier le lien de ce lot")}
            </button>
          )}
        </div>
        <div className="mt-12">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#D3CEC4]/60">{t("Look up another jar", "Consulter un autre pot")}</p>
          <div className="mt-4">
            <BatchForm batch={batch} setBatch={setBatch} busy={busy} t={t}
              onLookup={() => batch.trim() && navigate(`/batch/${encodeURIComponent(batch.trim())}`)} />
          </div>
        </div>
        <Link to="/shop" className="mt-12 inline-block text-[11px] uppercase tracking-[0.2em] text-[#B5A642]" data-testid="batch-back-shop">
          {t("Back to the Pawthecary", "Retour à la Pawthécaire")}
        </Link>
      </main>
    </LegacyShell>
  );
}
